import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { type ReactElement } from 'react';
import { TooltipTrigger } from 'react-aria-components';
import { fromEntries as rFromEntries, map as rMap, pick as rPick, pipe as rPipe } from 'remeda';
import {
  boolean as vBoolean,
  fallback as vFallback,
  nullish as vNullish,
  number as vNumber,
  object as vObject,
  string as vString,
  unknown as vUnknown,
  array as vArray,
} from 'valibot';

import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { LOCAL_STORAGE } from '@/constants/storage';
import {
  AddPlusStrokeIcon,
  IconButton,
  SegmentedButton,
  SegmentedButtonGroup,
  type SkeletonRow,
  Tooltip,
  useLocalStorage,
  useMediaQuery,
} from '@/libs/serenity-ui';
import { breakpoints } from '@/libs/serenity-ui/constants.stylex';
import { campaignRequestProgramQueries, type CampaignRequestProgramListQueryResultData } from '@/resources/campaign-request-program/campaign-request-program.api';
import { toApiProgramFieldAdapter } from '@/resources/campaign-request-program/campaign-request-program.field-adapter';
import { programFilterListAdapter } from '@/resources/campaign-request-program/campaign-request-program.filter-list-adapter';
import { campaignRequestProgramPolicies } from '@/resources/campaign-request-program/campaign-request-program.policies';
import { campaignRequestProgramTableOptions } from '@/resources/campaign-request-program/campaign-request-program.table';
import type { ListRouteContext } from '@/routes/_main/_list';
import { ActiveFilters } from '@/routes/_main/_list/-components/active-filters.component';
import { FullscreenTable } from '@/routes/_main/_list/-components/fullscreen-table.component';
import { Pagination } from '@/routes/_main/_list/-components/pagination.component';
import { Search } from '@/routes/_main/_list/-components/search.component';
import { ProgramFiltersForm } from '@/routes/_main/_list/programs/-components/campaign-request-program-filters-form.component';
import { Resource } from '@/types/resource.type';

const DEFAULT_SORTING = { id: 'updatedAt', desc: true };
const DEFAULT_PAGING = { pageIndex: 0, pageSize: 100 };
const SEARCH_FILTER_ID = 'name';

export const Route = createFileRoute('/_main/_list/programs/')({
  component: RouteComponent,
  validateSearch: vObject({
    pagination: vFallback(vNullish(vObject({ pageIndex: vNumber(), pageSize: vNumber() }), DEFAULT_PAGING), DEFAULT_PAGING),
    columnFilters: vFallback(vNullish(vArray(vObject({ id: vString(), value: vUnknown() })), []), []),
    sorting: vFallback(vNullish(vObject({ id: vString(), desc: vBoolean() }), DEFAULT_SORTING), DEFAULT_SORTING),
  }),
  beforeLoad: ({ search, context }) => {
    if (context.user && !context.programsFeatureEnabled) {
      throw redirect({ to: '/campaigns' });
    }

    if (context.user && !context.can.campaignRequestPrograms.view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: Resource.campaignRequestProgram as const,
      can: {
        [Resource.campaignRequestProgram as const]: rPick(
          campaignRequestProgramPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }),
          [ 'create' ],
        ),
      },
      queryOptions: {
        list: campaignRequestProgramQueries.list({
          paging: search.pagination,
          sorting: search.sorting,
          filter: toApiProgramFieldAdapter.transform(rPipe(
            search.columnFilters,
            rMap(({ id, value }) => [ id, value ] as const),
            rFromEntries(),
          )),
        }),
      },
    } satisfies Partial<ListRouteContext>;
  },
  loader: ({ context: { queryClient, queryOptions }}) => {
    queryClient.prefetchQuery(queryOptions.list);
  },
  staticData: {
    list: {
      heading: 'Programs',
      slots: {
        toolbarLeftComponent: ToolbarLeftComponent,
        toolbarActionsComponent: ToolbarActionsComponent,
        bottomBarComponent: Pagination,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Programs',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  const routeContext = Route.useRouteContext();

  const [ columnsConfig ] = useLocalStorage(
    LOCAL_STORAGE.columnsConfig(routeContext.resource).key,
    campaignRequestProgramTableOptions.initialConfig,
  );

  const query = useQuery(routeContext.queryOptions.list);

  const config = campaignRequestProgramTableOptions(query.data?.data ?? [], columnsConfig);
  const rows: (CampaignRequestProgramListQueryResultData | SkeletonRow)[] = query.isLoading
    ? config.getSkeletonRows()
    : query.data?.data ?? [];

  return (
    <FullscreenTable
      ariaLabel="Programs"
      config={config}
      getRowId={(row) => row.original.id.toString()}
      isLoading={query.isLoading}
      rows={rows}
    />
  );
}

function ToolbarActionsComponent(): ReactElement | false {
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const isSmallScreen = useMediaQuery(breakpoints.small);

  return isSmallScreen
    ? (
      <SegmentedButtonGroup
        disallowEmptySelection
        selectedKeys={[ 'programs' ]}
        selectionMode="single"
        onSelectionChange={() => navigate({ to: '/campaigns' })}>
        <SegmentedButton id="campaigns">Campaigns</SegmentedButton>
        <SegmentedButton id="programs">Programs</SegmentedButton>
      </SegmentedButtonGroup>
    )
    : (
      <>
        {routeContext.can.campaignRequestProgram.create() && (
          <TooltipTrigger>
            <IconButton aria-label="Create program" onPress={() => navigate({ to: '/programs/create' })}>
              <AddPlusStrokeIcon />
            </IconButton>
            <Tooltip>Create program</Tooltip>
          </TooltipTrigger>
        )}
        <SecondaryActionsIconButton isTableActions />
      </>
    );
}

function ToolbarLeftComponent(): ReactElement {
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const isSmallScreen = useMediaQuery(breakpoints.small);

  return (
    <>
      {!isSmallScreen &&
        <SegmentedButtonGroup
          disallowEmptySelection
          selectedKeys={[ 'programs' ]}
          selectionMode="single"
          onSelectionChange={() => navigate({ to: '/campaigns' })}>
          <SegmentedButton id="campaigns">Campaigns</SegmentedButton>
          <SegmentedButton id="programs">Programs</SegmentedButton>
        </SegmentedButtonGroup>}
      <Search resource={routeContext.resource} searchFilterId={SEARCH_FILTER_ID}>
        {(renderProps) => <ProgramFiltersForm onClose={renderProps.close} />}
      </Search>
      <ActiveFilters
        filterListAdapter={(filters) => programFilterListAdapter(filters)}
        searchFilterId={SEARCH_FILTER_ID}
      />
      {isSmallScreen &&
        <>
          {routeContext.can.campaignRequestProgram.create() && (
            <TooltipTrigger>
              <IconButton aria-label="Create program" onPress={() => navigate({ to: '/programs/create' })}>
                <AddPlusStrokeIcon />
              </IconButton>
              <Tooltip>Create program</Tooltip>
            </TooltipTrigger>
          )}
          <SecondaryActionsIconButton isTableActions />
        </>}
    </>
  );
}
